'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface PrayerRequestFormProps {
  onSubmit: (data: PrayerFormData) => void;
}

interface PrayerFormData {
  name: string;
  email: string;
  prayer: string;
  anonymous: boolean;
}

const PrayerRequestForm = ({ onSubmit }: PrayerRequestFormProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [formData, setFormData] = useState<PrayerFormData>({
    name: '',
    email: '',
    prayer: '',
    anonymous: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    return (
      <div className="bg-card rounded-lg p-8 shadow-sacred-md border border-border">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-surface rounded w-3/4" />
          <div className="h-10 bg-surface rounded" />
          <div className="h-10 bg-surface rounded" />
          <div className="h-32 bg-surface rounded" />
        </div>
      </div>
    );
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    setTimeout(() => {
      onSubmit(formData);
      setIsSubmitting(false);
      setSubmitSuccess(true);
      setFormData({ name: '', email: '', prayer: '', anonymous: false });
      
      setTimeout(() => setSubmitSuccess(false), 3000);
    }, 1000);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  return (
    <div className="bg-card rounded-lg p-8 shadow-sacred-md border border-border">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
          <Icon name="HandRaisedIcon" size={24} className="text-primary" />
        </div>
        <div>
          <h3 className="font-playfair text-2xl font-semibold text-foreground">
            Submit Prayer Request
          </h3>
          <p className="font-inter text-sm text-muted-foreground">
            Share your prayer needs with our community
          </p>
        </div>
      </div>

      {submitSuccess && (
        <div className="mb-6 p-4 bg-success/10 border border-success/30 rounded-lg flex items-center space-x-3">
          <Icon name="CheckCircleIcon" size={20} className="text-success" variant="solid" />
          <p className="font-inter text-sm text-success font-medium">
            Your prayer request has been received. Our community will pray for you.
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label htmlFor="name" className="block font-inter text-sm font-medium text-foreground mb-2">
            Your Name
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            disabled={formData.anonymous}
            className="w-full px-4 py-3 bg-input border border-border rounded-lg font-inter text-sm text-foreground placeholder-muted focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            placeholder="Enter your full name"
          />
        </div>

        <div>
          <label htmlFor="email" className="block font-inter text-sm font-medium text-foreground mb-2">
            Email Address
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 bg-input border border-border rounded-lg font-inter text-sm text-foreground placeholder-muted focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
            placeholder="your.email@example.com"
          />
        </div>

        <div>
          <label htmlFor="prayer" className="block font-inter text-sm font-medium text-foreground mb-2">
            Prayer Request
          </label>
          <textarea
            id="prayer"
            name="prayer"
            value={formData.prayer}
            onChange={handleChange}
            required
            rows={5}
            className="w-full px-4 py-3 bg-input border border-border rounded-lg font-inter text-sm text-foreground placeholder-muted focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 resize-none"
            placeholder="Share your prayer request with our community..."
          />
        </div>

        <div className="flex items-center space-x-3">
          <input
            type="checkbox"
            id="anonymous"
            name="anonymous"
            checked={formData.anonymous}
            onChange={handleChange}
            className="w-5 h-5 text-primary bg-input border-border rounded focus:ring-2 focus:ring-primary cursor-pointer"
          />
          <label htmlFor="anonymous" className="font-inter text-sm text-foreground cursor-pointer">
            Submit anonymously (your name will not be shared)
          </label>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full px-6 py-4 bg-primary text-primary-foreground font-inter text-base font-semibold rounded-lg hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
        >
          {isSubmitting ? (
            <>
              <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
              <span>Submitting...</span>
            </>
          ) : (
            <>
              <Icon name="PaperAirplaneIcon" size={20} />
              <span>Submit Prayer Request</span>
            </>
          )}
        </button>
      </form>
    </div>
  );
};

export default PrayerRequestForm;